import { useEffect, useState } from 'react';

import { api } from './api/client';
import { ChartRenderer } from './components/ChartRenderer';
import { ChartStudio } from './components/ChartStudio';
import { DashboardBuilder } from './components/DashboardBuilder';
import { WidgetBuilder } from './components/WidgetBuilder';
import { DashboardView, WidgetView } from './routes/DashboardView';
import type { SavedChart, SavedDashboard, SavedWidget } from './types';

/**
 * Two things live here: the embed routes the platform points at (`/d/…`,
 * `/w/…`), and the authoring UI everything else falls through to.
 *
 * Routing is a path match rather than a router dependency — the app has three
 * shapes, and a library would be more moving parts than the problem needs.
 */

type Tab = 'charts' | 'dashboards' | 'widgets';
type Editor =
  | { kind: 'chart'; value: SavedChart | null }
  | { kind: 'dashboard'; value: SavedDashboard | null }
  | { kind: 'widget'; value: SavedWidget | null }
  | null;

const Authoring = () => {
  const [tab, setTab] = useState<Tab>('charts');
  const [charts, setCharts] = useState<SavedChart[]>([]);
  const [dashboards, setDashboards] = useState<SavedDashboard[]>([]);
  const [widgets, setWidgets] = useState<SavedWidget[]>([]);
  const [editor, setEditor] = useState<Editor>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = () => {
    Promise.all([api.charts(), api.dashboards(), api.widgets()])
      .then(([c, d, w]) => {
        setCharts(c);
        setDashboards(d);
        setWidgets(w);
      })
      .catch((err: Error) => setError(err.message));
  };

  useEffect(refresh, []);

  const closeEditor = () => {
    setEditor(null);
    refresh();
  };

  if (editor?.kind === 'chart') {
    return <ChartStudio editing={editor.value} onSaved={closeEditor} onCancel={() => setEditor(null)} />;
  }
  if (editor?.kind === 'dashboard') {
    return (
      <DashboardBuilder
        charts={charts}
        editing={editor.value}
        onSaved={closeEditor}
        onCancel={() => setEditor(null)}
      />
    );
  }
  if (editor?.kind === 'widget') {
    return (
      <WidgetBuilder
        charts={charts}
        editing={editor.value}
        onSaved={closeEditor}
        onCancel={() => setEditor(null)}
      />
    );
  }

  return (
    <div className="app">
      <header className="app__header">
        <h1>appLockr Reports</h1>
        <nav>
          {(['charts', 'dashboards', 'widgets'] as Tab[]).map((t) => (
            <button key={t} className={tab === t ? 'active' : ''} onClick={() => setTab(t)}>
              {t[0].toUpperCase() + t.slice(1)}
            </button>
          ))}
        </nav>
      </header>

      {error && <p className="error">{error}</p>}

      {tab === 'charts' && (
        <section>
          <div className="section-head">
            <h2>Charts</h2>
            <button className="primary" onClick={() => setEditor({ kind: 'chart', value: null })}>
              New chart
            </button>
          </div>
          {charts.length === 0 && <p className="hint">No charts yet.</p>}
          <div className="card-grid">
            {charts.map((c) => (
              <article className="card" key={c.id}>
                <header>
                  <strong>{c.name}</strong>
                  <span className="badge">{c.kind === 'custom' ? 'AI' : c.template}</span>
                </header>
                <div className="card__body">
                  <ChartRenderer chart={c} />
                </div>
                <footer>
                  <button onClick={() => setEditor({ kind: 'chart', value: c })}>Edit</button>
                  <button
                    onClick={async () => {
                      await api.deleteChart(c.id);
                      refresh();
                    }}
                  >
                    Delete
                  </button>
                </footer>
              </article>
            ))}
          </div>
        </section>
      )}

      {tab === 'dashboards' && (
        <section>
          <div className="section-head">
            <h2>Dashboards</h2>
            <button className="primary" onClick={() => setEditor({ kind: 'dashboard', value: null })}>
              New dashboard
            </button>
          </div>
          {dashboards.length === 0 && <p className="hint">No dashboards yet.</p>}
          <ul className="list">
            {dashboards.map((d) => (
              <li key={d.id}>
                <div>
                  <strong>{d.name}</strong>
                  <code>/d/{d.slug}</code>
                  <span className="hint">
                    {d.tiles.length} tile{d.tiles.length === 1 ? '' : 's'} · filter: {d.appFilter}
                  </span>
                </div>
                <div>
                  <a href={`/d/${d.slug}`} target="_blank" rel="noreferrer">
                    Open
                  </a>
                  <button onClick={() => setEditor({ kind: 'dashboard', value: d })}>Edit</button>
                  <button
                    onClick={async () => {
                      await api.deleteDashboard(d.id);
                      refresh();
                    }}
                  >
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {tab === 'widgets' && (
        <section>
          <div className="section-head">
            <h2>Widgets</h2>
            <button className="primary" onClick={() => setEditor({ kind: 'widget', value: null })}>
              New widget
            </button>
          </div>
          {widgets.length === 0 && <p className="hint">No widgets yet.</p>}
          <ul className="list">
            {widgets.map((w) => (
              <li key={w.id}>
                <div>
                  <strong>{w.name}</strong>
                  <code>/w/{w.id}</code>
                  <span className="hint">{w.aspectRatio}</span>
                </div>
                <div>
                  <a href={`/w/${w.id}`} target="_blank" rel="noreferrer">
                    Open
                  </a>
                  <button onClick={() => setEditor({ kind: 'widget', value: w })}>Edit</button>
                  <button
                    onClick={async () => {
                      await api.deleteWidget(w.id);
                      refresh();
                    }}
                  >
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};

export const App = () => {
  const path = window.location.pathname;

  const dashboard = path.match(/^\/d\/([a-z0-9-]+)\/?$/);
  if (dashboard) return <DashboardView slug={dashboard[1]} />;

  const widget = path.match(/^\/w\/([A-Za-z0-9_-]+)\/?$/);
  if (widget) return <WidgetView widgetId={widget[1]} />;

  return <Authoring />;
};
